import React from "react";

/*
 * Catches render errors so a failing page shows a readable message instead of a
 * blank screen.
 *
 * This matters most for /assets-map, which RouteGuard renders WITHOUT
 * AdminLayout — it opens in a new tab via window.open, so a thrown error left a
 * completely white tab with nothing to diagnose from. "The map doesn't work"
 * and "the map crashed on line N" are very different bug reports.
 *
 * Class component because React only supports error boundaries as classes;
 * there is no hook equivalent.
 */
class ErrorBoundary extends React.Component {
	constructor(props) {
		super(props);
		this.state = { error: null };
	}

	static getDerivedStateFromError(error) {
		return { error };
	}

	componentDidCatch(error, info) {
		// Goes to the browser console, which is where someone debugging a blank
		// tab will actually look.
		console.error("[ErrorBoundary]", this.props.label || "page", error, info?.componentStack);
	}

	render() {
		const { error } = this.state;
		if (!error) return this.props.children;

		return (
			<div
				style={{
					minHeight: "100vh",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					padding: 24,
					background: "#f5f7fa",
					fontFamily: "system-ui, -apple-system, 'Segoe UI', sans-serif",
				}}
			>
				<div
					style={{
						maxWidth: 560,
						background: "#ffffff",
						border: "1px solid rgba(11,11,11,0.10)",
						borderRadius: 10,
						padding: "24px 28px",
					}}
				>
					<h1 style={{ fontSize: 18, margin: "0 0 8px", color: "#0b0b0b" }}>
						{this.props.label ? `${this.props.label} failed to load` : "Something went wrong"}
					</h1>
					<p style={{ fontSize: 14, color: "#52514e", margin: "0 0 16px", lineHeight: 1.5 }}>
						The page hit an unexpected error. Reloading often clears it. If it keeps happening, the
						details below identify the cause.
					</p>

					<button
						type="button"
						onClick={() => window.location.reload()}
						style={{
							background: "#0948b3",
							color: "#ffffff",
							border: "none",
							borderRadius: 6,
							padding: "9px 18px",
							fontSize: 14,
							fontWeight: 600,
							cursor: "pointer",
						}}
					>
						Reload
					</button>

					<pre
						style={{
							marginTop: 18,
							padding: 12,
							background: "#f5f7fa",
							border: "1px solid rgba(11,11,11,0.08)",
							borderRadius: 6,
							fontSize: 12,
							color: "#6b4e00",
							whiteSpace: "pre-wrap",
							wordBreak: "break-word",
							maxHeight: 180,
							overflow: "auto",
						}}
					>
						{String(error?.message || error)}
					</pre>
				</div>
			</div>
		);
	}
}

export default ErrorBoundary;
